import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from 'src/components/ui/dialog';
import { PortalDsButton } from 'src/components/portal-design-system/portal-ds-button';
import { PortalDsField, PortalDsInput } from 'src/components/portal-design-system/portal-ds-field';
import { portalDs } from 'src/components/portal-design-system/portal-ds-classes';
import { Client } from '../../types/client.types';
import { useDeleteClientMutation } from '../../endpoints/client.endpoints';

interface DeleteClientModalProps {
  open: boolean;
  client: Client | null;
  onOpenChange: (open: boolean) => void;
  onDeleted: () => void;
}

export const DeleteClientModal: React.FC<DeleteClientModalProps> = ({
  open,
  client,
  onOpenChange,
  onDeleted,
}) => {
  const [confirmName, setConfirmName] = useState('');
  const [deleteClient, { isLoading }] = useDeleteClientMutation();

  useEffect(() => {
    if (!open) {
      setConfirmName('');
    }
  }, [open]);

  if (!client) {
    return null;
  }

  const userCount = client.users?.length ?? 0;
  const accessCount = client.access?.length ?? 0;
  const nameMatches = confirmName.trim() === client.title;
  const canConfirm = nameMatches && !isLoading;

  const handleDelete = async() => {
    if (!canConfirm) {
      return;
    }
    try {
      await deleteClient({ clientId: client.ext_client_id }).unwrap();
      onOpenChange(false);
      onDeleted();
    } catch (error) {
      console.error('Failed to delete client:', error);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent
        className="sm:max-w-md bg-white"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogTitle className={portalDs.sectionTitle}>
          Delete client
        </DialogTitle>
        <p className={portalDs.sheetDescription}>
          You are about to delete
          {' '}
          <strong>
            {client.title}
          </strong>
          . Client users will lose access.
          This will archive
          {' '}
          {userCount}
          {' '}
          user
          {userCount === 1 ? '' : 's'}
          {' '}
          and
          {' '}
          {accessCount}
          {' '}
          account
          access
          {' '}
          {accessCount === 1 ? 'entry' : 'entries'}
          .
        </p>
        <PortalDsField
          label={`Type "${client.title}" to confirm`}
          htmlFor="delete-client-confirm-name"
        >
          <PortalDsInput
            id="delete-client-confirm-name"
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            placeholder={client.title}
            autoComplete="off"
          />
        </PortalDsField>
        <DialogFooter className="gap-2 sm:gap-0">
          <PortalDsButton
            variant="secondary"
            size="sm"
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </PortalDsButton>
          <PortalDsButton
            variant="danger"
            size="sm"
            type="button"
            onClick={() => void handleDelete()}
            disabled={!canConfirm}
          >
            Delete client
          </PortalDsButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteClientModal;
